import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import BaseButton from "./BaseButton/BaseButton";
import { canGoBackInsideApp } from "../navigation/history";
import { getBackTarget, PAYMENT_BACK_EVENT, shouldShowBrowserNavigation } from "../navigation/routes";

const BOTTOM_NAV_TRANSITION_MS = 320;

const NavigationHeaderContext = React.createContext({
    shouldShowNavigation: false,
    onBack: () => {},
    setBottomActions: () => {},
    setBottomBackAction: () => {},
    setBottomNavClassName: () => {},
    setBottomPanel: () => {},
});

export function useNavigationHeader() {
    return React.useContext(NavigationHeaderContext);
}

function MainLayout() {
    const { hasTelegramWebApp, isTelegram } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [bottomActions, setBottomActions] = React.useState(null);
    const [bottomBackAction, setBottomBackAction] = React.useState(null);
    const [bottomNavClassName, setBottomNavClassName] = React.useState("");
    const [isBottomNavVisible, setIsBottomNavVisible] = React.useState(false);
    const [bottomNavHeight, setBottomNavHeight] = React.useState(0);
    const bottomNavRef = React.useRef(null);
    const bottomPanelRef = React.useRef({ actions: null, backAction: null });
    const transitionTimeoutRef = React.useRef(null);
    const revealFrameRef = React.useRef(null);
    const pathname = location.pathname;
    const shouldShowNavigation = !hasTelegramWebApp && shouldShowBrowserNavigation(pathname);

    const handleBack = React.useCallback(() => {
        if (pathname === "/payment") {
            window.dispatchEvent(new CustomEvent(PAYMENT_BACK_EVENT));
            return;
        }

        if (canGoBackInsideApp()) {
            navigate(-1);
            return;
        }

        const backTarget = getBackTarget(pathname);
        if (backTarget) {
            navigate(backTarget, { replace: true });
            return;
        }

        if (pathname !== "/") {
            navigate("/", { replace: true });
        }
    }, [navigate, pathname]);

    const applyBottomPanel = React.useCallback((panel) => {
        const nextPanel = {
            actions: panel?.actions || null,
            backAction: panel?.backAction || null,
        };
        const currentPanel = bottomPanelRef.current;
        const shouldTransition = Boolean(panel?.transition && currentPanel.actions && nextPanel.actions);

        if (transitionTimeoutRef.current) {
            window.clearTimeout(transitionTimeoutRef.current);
        }

        if (revealFrameRef.current) {
            window.cancelAnimationFrame(revealFrameRef.current);
        }

        if (!shouldTransition) {
            bottomPanelRef.current = nextPanel;
            setBottomActions(nextPanel.actions);
            setBottomBackAction(nextPanel.backAction);
            setBottomNavClassName("");
            return;
        }

        setBottomNavClassName("app-bottom-nav-hidden");
        transitionTimeoutRef.current = window.setTimeout(() => {
            bottomPanelRef.current = nextPanel;
            setBottomActions(nextPanel.actions);
            setBottomBackAction(nextPanel.backAction);

            revealFrameRef.current = window.requestAnimationFrame(() => {
                setBottomNavClassName("");
            });
        }, BOTTOM_NAV_TRANSITION_MS);
    }, []);

    const updateBottomActions = React.useCallback((actions) => {
        applyBottomPanel({ actions, backAction: null });
    }, [applyBottomPanel]);

    const updateBottomBackAction = React.useCallback((backAction) => {
        bottomPanelRef.current = {
            ...bottomPanelRef.current,
            backAction,
        };
        setBottomBackAction(backAction);
    }, []);

    const navigationValue = React.useMemo(() => ({
        shouldShowNavigation,
        onBack: handleBack,
        setBottomActions: updateBottomActions,
        setBottomBackAction: updateBottomBackAction,
        setBottomNavClassName,
        setBottomPanel: applyBottomPanel,
    }), [applyBottomPanel, handleBack, shouldShowNavigation, updateBottomActions, updateBottomBackAction]);

    const appClassName = [
        "main_width flex-col flex-start app gap1",
        isTelegram ? "app-telegram" : "",
        shouldShowNavigation ? "app-with-bottom-nav" : "",
        shouldShowNavigation && bottomActions ? "app-with-bottom-actions" : "",
    ].filter(Boolean).join(" ");

    React.useLayoutEffect(() => {
        const bottomNavElement = bottomNavRef.current;

        if (!shouldShowNavigation || !bottomNavElement) {
            setBottomNavHeight(0);
            return undefined;
        }

        const updateBottomNavHeight = () => {
            setBottomNavHeight(Math.ceil(bottomNavElement.getBoundingClientRect().height));
        };

        updateBottomNavHeight();

        if (typeof ResizeObserver === "undefined") {
            return undefined;
        }

        const resizeObserver = new ResizeObserver(updateBottomNavHeight);
        resizeObserver.observe(bottomNavElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [bottomActions, shouldShowNavigation]);

    React.useEffect(() => {
        if (!shouldShowNavigation) {
            setIsBottomNavVisible(false);
            return undefined;
        }

        setIsBottomNavVisible(false);
        const frameId = window.requestAnimationFrame(() => {
            setIsBottomNavVisible(true);
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [pathname, shouldShowNavigation]);

    React.useEffect(() => () => {
        if (transitionTimeoutRef.current) {
            window.clearTimeout(transitionTimeoutRef.current);
        }

        if (revealFrameRef.current) {
            window.cancelAnimationFrame(revealFrameRef.current);
        }
    }, []);

    return (
        <NavigationHeaderContext.Provider value={navigationValue}>
            <div className="app-shell">
                <div className={appClassName}>
                    <Outlet />
                    {shouldShowNavigation ? (
                        <div
                            className={`app-bottom-nav-spacer${bottomActions ? " app-bottom-nav-spacer-with-actions" : ""}`}
                            style={{ "--app-bottom-nav-height": `${bottomNavHeight}px` }}
                            aria-hidden="true"
                        />
                    ) : null}
                    {shouldShowNavigation ? (
                        <div
                            ref={bottomNavRef}
                            className={[
                                "app-bottom-nav",
                                isBottomNavVisible ? "app-bottom-nav-visible" : "",
                                bottomNavClassName,
                            ].filter(Boolean).join(" ")}
                            aria-label="Навигация"
                        >
                            {bottomActions}
                            <BaseButton
                                type="button"
                                className="app-bottom-nav-btn"
                                onClick={bottomBackAction?.onClick || handleBack}
                                contentAlign="center"
                                showArrow={false}
                            >
                                <span>{bottomBackAction?.label || "Назад"}</span>
                            </BaseButton>
                        </div>
                    ) : null}
                </div>
            </div>
        </NavigationHeaderContext.Provider>
    );
}

export default MainLayout;
